import { keyBy } from 'lodash';
import { types, flow, Instance, SnapshotIn, SnapshotOut } from 'mobx-state-tree';
import { ApiStore } from '@src/features/common';
import { handleRequest } from 'src/helpers';
import { reorderStep } from '@src/services/jobs';

const CaTestAnswerDocument = types.model({
  id: types.maybeNull(types.number),
  title: types.maybeNull(types.string),
  file_url: types.maybeNull(types.frozen()),
  user_test_id: types.maybeNull(types.number),
  ca_test_id: types.maybeNull(types.number),
  user_id: types.maybeNull(types.number),
});

const CaTest = types.model({
  id: types.maybeNull(types.number),
  title: types.maybeNull(types.string),
  test_type: types.maybeNull(types.string),
});

const UserTest = types.model({
  id: types.maybeNull(types.number),
  state: types.maybeNull(types.string),
  score: types.maybeNull(types.number),
  started_at: types.maybeNull(types.string),
  created_at: types.maybeNull(types.string),
  timeframe_to_pass: types.maybeNull(types.number),
  test_type: types.maybeNull(types.string),
  subject: types.maybeNull(types.string),
  test: types.maybeNull(CaTest),
  ca_test_answers_documents: types.array(CaTestAnswerDocument),
});

const Applicant = types.model({
  id: types.identifierNumber,
  job_id: types.maybeNull(types.number),
  user_id: types.maybeNull(types.number),
  photo: types.maybeNull(
    types.frozen({
      url: types.string,
      thumb: {
        url: types.string,
      },
    }),
  ),
  resume: types.maybeNull(
    types.frozen({
      url: types.string,
    }),
  ),
  source_id: types.maybe(types.number),
  step_id: types.maybeNull(types.number),
  recruited_id: types.maybeNull(types.number),
  state: types.maybeNull(types.string),
  email: types.string,
  first_name: types.maybeNull(types.string),
  last_name: types.maybeNull(types.string),
  full_photo_url: types.maybeNull(types.string),
  phone: types.maybeNull(types.string),
  country: types.maybeNull(types.string),
  city: types.maybeNull(types.string),
  street: types.maybeNull(types.string),
  postcode: types.maybeNull(types.string),
  custom_answers: types.maybeNull(types.frozen({})),
  cover_letter: types.maybeNull(types.string),
  general: types.maybeNull(types.frozen({})),
  is_first_internship: types.maybeNull(types.boolean),
  has_convention: types.maybeNull(types.boolean),
  tags: types.maybeNull(
    types.array(
      types.frozen({
        tag_id: types.number,
      }),
    ),
  ),
  tests: types.array(UserTest),
  interests_by_freelancer: types.maybeNull(
    types.array(
      types.frozen({
        id: types.number,
      }),
    ),
  ),
  languages: types.array(types.frozen()),
  experiences: types.array(types.frozen()),
  degrees: types.array(types.frozen()),
  created_at: types.maybeNull(types.string),
  last_contacted_at: types.maybeNull(types.string),
  match_score: types.maybeNull(types.number),
  user: types.frozen({}),
  assigned_fdc_subjects: types.maybeNull(types.array(types.string)),
});

export const ApplicantsStore = types
  .compose(
    types.model({
      applicants: types.map(Applicant),
    }),
    ApiStore,
  )
  .actions((self) => {
    const updateStateLocally = (updatedApplicantList: Array<any>) => {
      updatedApplicantList.forEach((updatedApplicant) => {
        const applicant = self.applicants.get(updatedApplicant.id);
        if (applicant) {
          self.applicants.set(updatedApplicant.id, { ...applicant, ...updatedApplicant });
        }
      });
    };

    return {
      fetchApplicant: flow(function* (API, ...params) {
        self.countPendingCalls++;
        try {
          const applicant = yield API.getApplicant(...params);
          self.applicants.set(applicant.id, applicant);
        } finally {
          self.countPendingCalls--;
        }
      }),
      fetchAllApplicants: flow(function* (API) {
        const { applicants: applicantsArray } = yield API.getApplicants();
        const applicantsMap = keyBy(applicantsArray, 'id');
        self.applicants.merge(applicantsMap);
      }),
      updateApplicantCache: function (id: string, updatedCache: any) {
        const cache = self.applicants.get(id);
        if (cache) {
          self.applicants.set(id, { ...cache, ...updatedCache });
        }
      },
      deleteApplicants: flow(function* (API, params, payload) {
        const request = API.deleteApplicants(params, payload);
        yield handleRequest(request, true);
        const applicantsIdsToRemove = payload.ids;
        applicantsIdsToRemove.forEach((id) => self.applicants.delete(id));
      }),
      // write doc
      reorderStep: flow(function* ({ currStepId, jobId, nextStepId, onError }) {
        yield self.patch(
          reorderStep(jobId),
          {
            current_step_id: currStepId,
            next_step_id: nextStepId,
          },
          {
            onFetchError: () => {
              onError();
            },
          },
        );
      }),
      updateStep: flow(function* (API, payload) {
        const request = API.updateStep(payload);
        const { applicants } = yield handleRequest(request, false);
        if (!Array.isArray(applicants)) return;
        applicants.forEach((updatedApplicant) => {
          const applicant = self.applicants.get(updatedApplicant.id);
          if (applicant) {
            self.applicants.set(updatedApplicant.id, { ...applicant, ...updatedApplicant });
          }
        });
      }),
      updateState: flow(function* (API, payload) {
        const request = API.updateState(payload);
        const applicants = yield handleRequest(request, true);
        updateStateLocally(applicants);
      }),

      updateStateLocally,

      updateTags: function (applicantId, tags) {
        const applicant = self.applicants.get(applicantId);
        if (applicant) {
          applicant.tags = tags;
        }
      },
    };
  })
  .actions((self) => ({
    fetch: flow(function* (API, ...params) {
      if (!params.find((param) => param.applicantId)) return;
      yield self.fetchApplicant(API, ...params);
      yield self.fetchAllApplicants(API);
    }),
  }))
  .views((self) => ({
    get applicantsState() {
      return {
        isFetching: Boolean(self.isFetching),
        applicants: Array.from(self.applicants.values()),
      };
    },
  }));

export type IApplicantStore = Instance<typeof ApplicantsStore>;
export type IApplicantStoreSnap = SnapshotIn<typeof ApplicantsStore>;
export type IApplicantStoreSnapOut = SnapshotOut<typeof ApplicantsStore>;
